/**
 * Per-field validators for the editable phx-api-v2 Lambda fields the AI copilot is allowed to
 * change (edit flow). The backend planner re-validates every `setField` value against this
 * registry before returning a plan, so an out-of-range value the model produces is rejected
 * server-side rather than driven into the UI.
 *
 * Isomorphic: lives in config-editor-common so the same bounds back the frontend spinners
 * (LAMBDA_*_INPUT_PROPS in normalizeApiConfig) and the backend validation.
 */

import {
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from './normalizeApiConfig';
import { isRecord } from './summarizeConfig';

export type FieldValidationResult =
  | { ok: true; value: number | boolean | string | Record<string, string> }
  | { ok: false; error: string };

/**
 * Runtime / architecture allowlists. These ARE the lists the phx-api-v2 Visual Editor renders
 * (RuntimeArchitectureFields feeds them straight to EnumSelect), so the dropdown cannot offer a
 * value the backend planner then rejects.
 */
export const LAMBDA_RUNTIMES = [
  'dotnet8',
  'java17',
  'java21',
  'nodejs18.x',
  'nodejs20.x',
  'python3.9',
  'python3.11',
  'python3.12',
  'python3.13',
  'python3.14',
];
export const LAMBDA_ARCHITECTURES = ['arm64', 'x86_64'];

export interface FieldValidator {
  /** Config field name as it appears in config.json (e.g. `memory_size`). */
  field: string;
  /** Human description of the accepted range/shape (surfaced in errors + the system prompt). */
  describe: string;
  validate: (value: unknown) => FieldValidationResult;
}

const ENV_VAR_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Lambda memory: integer MB within Lambda's bounds, aligned to the editor's 64 MB step. */
const memorySize: FieldValidator = {
  field: 'memory_size',
  describe: `integer MB, ${LAMBDA_MEMORY_MB_INPUT_PROPS.min}-${LAMBDA_MEMORY_MB_INPUT_PROPS.max}, multiple of ${LAMBDA_MEMORY_MB_INPUT_PROPS.step}`,
  validate: value => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return { ok: false, error: 'memory_size must be a number' };
    }
    if (!Number.isInteger(value)) {
      return { ok: false, error: 'memory_size must be an integer' };
    }
    const { min, max, step } = LAMBDA_MEMORY_MB_INPUT_PROPS;
    if (value < min || value > max) {
      return { ok: false, error: `memory_size must be between ${min} and ${max} MB` };
    }
    if (value % step !== 0) {
      return { ok: false, error: `memory_size must be a multiple of ${step} MB` };
    }
    return { ok: true, value };
  },
};

/** Lambda timeout: integer seconds, 1-900. */
const timeout: FieldValidator = {
  field: 'timeout',
  describe: `integer seconds, ${LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS.min}-${LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS.max}`,
  validate: value => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return { ok: false, error: 'timeout must be a number' };
    }
    if (!Number.isInteger(value)) {
      return { ok: false, error: 'timeout must be an integer' };
    }
    const { min, max } = LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS;
    if (value < min || value > max) {
      return { ok: false, error: `timeout must be between ${min} and ${max} seconds` };
    }
    return { ok: true, value };
  },
};

/** SnapStart toggle: boolean, tolerating the "true"/"false" strings LLMs sometimes emit. */
const snapstart: FieldValidator = {
  field: 'snapstart',
  describe: 'boolean (true or false)',
  validate: value => {
    if (typeof value === 'boolean') return { ok: true, value };
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true') return { ok: true, value: true };
      if (v === 'false') return { ok: true, value: false };
    }
    return { ok: false, error: 'snapstart must be a boolean (true or false)' };
  },
};

/**
 * A single environment variable entry. The edit flow sets one env var at a time, so the value
 * is `{ key, value }` with a valid shell/Lambda env key and a string value.
 */
const envVars: FieldValidator = {
  field: 'env_vars',
  describe: 'object { key: <VALID_ENV_KEY>, value: <string> } (one variable at a time)',
  validate: value => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, error: 'env_vars must be an object { key, value }' };
    }
    const { key, value: val } = value as { key?: unknown; value?: unknown };
    if (typeof key !== 'string' || !ENV_VAR_KEY_RE.test(key)) {
      return {
        ok: false,
        error: 'env_vars.key must match /^[A-Za-z_][A-Za-z0-9_]*$/',
      };
    }
    if (typeof val !== 'string') {
      return { ok: false, error: 'env_vars.value must be a string' };
    }
    return { ok: true, value: { [key]: val } };
  },
};

/** A non-empty short string field (Lambda handler / FQCN). */
const lambdaHandler: FieldValidator = {
  field: 'lambda_handler',
  describe: 'non-empty string (the Lambda handler / FQCN)',
  validate: value => {
    if (typeof value !== 'string' || value.trim() === '') {
      return { ok: false, error: 'lambda_handler must be a non-empty string' };
    }
    if (value.length > 256) {
      return { ok: false, error: 'lambda_handler must be 256 characters or fewer' };
    }
    return { ok: true, value: value.trim() };
  },
};

/** The lambda source / artifact reference (e.g. a repo or build path). */
const lambdaSource: FieldValidator = {
  field: 'lambda_source',
  describe: 'non-empty string (the Lambda source / artifact reference)',
  validate: value => {
    if (typeof value !== 'string' || value.trim() === '') {
      return { ok: false, error: 'lambda_source must be a non-empty string' };
    }
    if (value.length > 512) {
      return { ok: false, error: 'lambda_source must be 512 characters or fewer' };
    }
    return { ok: true, value: value.trim() };
  },
};

/** Lambda runtime: one of the supported runtimes (mirrors the editor's RuntimeSelect). */
const runtime: FieldValidator = {
  field: 'runtime',
  describe: `one of: ${LAMBDA_RUNTIMES.join(', ')}`,
  validate: value =>
    typeof value === 'string' && LAMBDA_RUNTIMES.includes(value)
      ? { ok: true, value }
      : { ok: false, error: `runtime must be one of: ${LAMBDA_RUNTIMES.join(', ')}` },
};

/** Lambda CPU architecture: arm64 or x86_64. */
const architecture: FieldValidator = {
  field: 'architecture',
  describe: `one of: ${LAMBDA_ARCHITECTURES.join(', ')}`,
  validate: value =>
    typeof value === 'string' && LAMBDA_ARCHITECTURES.includes(value)
      ? { ok: true, value }
      : {
          ok: false,
          error: `architecture must be one of: ${LAMBDA_ARCHITECTURES.join(', ')}`,
        },
};

export const FIELD_VALIDATORS: Record<string, FieldValidator> = {
  memory_size: memorySize,
  timeout,
  snapstart,
  env_vars: envVars,
  lambda_handler: lambdaHandler,
  lambda_source: lambdaSource,
  runtime,
  architecture,
};

/** The set of Lambda fields the AI copilot may edit. */
export const EDITABLE_FIELDS = Object.keys(FIELD_VALIDATORS);

/**
 * Validate a value for an editable field. Returns `{ ok:false }` for unknown fields (the copilot
 * is not allowed to edit anything outside the registry) and for out-of-range/wrong-type values.
 */
export function validateFieldValue(field: string, value: unknown): FieldValidationResult {
  const validator = FIELD_VALIDATORS[field];
  if (!validator) {
    return { ok: false, error: `field "${field}" is not editable by the copilot` };
  }
  return validator.validate(value);
}

/* ------------------------------------------------------------------------------------------------
 * Write-path validation for a whole config.json.
 *
 * Deliberately NOT the registry above. That one backs the copilot's one-field-at-a-time edit flow:
 * its `env_vars` validator takes the copilot's `{key, value}` payload rather than the config's
 * `Record<string, string>`, its `lambda_source` refuses the empty string the phoenix template ships
 * in `defaults`, and its memory rule carries the editor spinner's 64 MB step. Reusing it wholesale
 * would refuse configs that are already in the repositories and deploy fine.
 *
 * So this checks TYPES, and only types. A string where Terraform needs a number is always a defect;
 * an unusual-but-valid number is not ours to refuse. `memory_size: "banana"` is rejected here,
 * `memory_size: 3008` is not — AWS Lambda accepts any integer in range, so the template's ten-value
 * enum is a curated UI list, not a constraint the config has to honour.
 * ---------------------------------------------------------------------------------------------- */

type TypeCheck = (value: unknown) => string | undefined;

const anInteger =
  (range: { min: number; max: number }): TypeCheck =>
  value => {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      return `must be an integer, got ${describeValue(value)}`;
    }
    return value < range.min || value > range.max
      ? `must be between ${range.min} and ${range.max}, got ${value}`
      : undefined;
  };

const aString: TypeCheck = value =>
  typeof value !== 'string' ? `must be a string, got ${describeValue(value)}` : undefined;

const aBoolean: TypeCheck = value =>
  typeof value !== 'boolean' ? `must be true or false, got ${describeValue(value)}` : undefined;

const aStringList: TypeCheck = value =>
  !Array.isArray(value) || value.some(v => typeof v !== 'string')
    ? `must be a list of strings, got ${describeValue(value)}`
    : undefined;

/** Condor stores one trigger path as a string and several as an array. Both are valid. */
const aStringOrStringList: TypeCheck = value =>
  aString(value) === undefined || aStringList(value) === undefined
    ? undefined
    : `must be a string or a list of strings, got ${describeValue(value)}`;

const aStringMap: TypeCheck = value =>
  !isRecord(value) || Object.values(value).some(v => typeof v !== 'string')
    ? `must be an object of string values, got ${describeValue(value)}`
    : undefined;

/**
 * Every scalar a lambda entry or a group `defaults` block can carry, across all four templates.
 * A key that is absent is never an error — absence is how a lambda inherits from `defaults`.
 */
const LAMBDA_FIELD_TYPES: Record<string, TypeCheck> = {
  // Ranges are the ones AWS itself enforces, so a value outside them fails at apply rather than
  // merely looking odd.
  memory_size: anInteger({ min: 128, max: 10240 }),
  timeout: anInteger({ min: 1, max: 900 }),
  ephemeral_storage: anInteger({ min: 512, max: 10240 }),
  snapstart: aBoolean,
  snap_alias: aBoolean,
  param_and_secrets_ext_enabled: aBoolean,
  datadog_lambda_apm_enabled: aBoolean,
  use_readonly_proxy: aBoolean,
  runtime: aString,
  architecture: aString,
  package_type: aString,
  lambda_handler: aString,
  lambda_source: aString,
  capacity_provider: aString,
  build_number: aString,
  s3_key: aString,
  dead_letter_queue: aString,
  alarms_sns_topic: aString,
  sqs_queue: aString,
  lambda_trigger_path: aStringOrStringList,
  env_vars: aStringMap,
  environment_variables: aStringMap,
  sqs_queue_urls: aStringMap,
  iam_policy_names: aStringList,
  iam_policy_names_custom: aStringList,
  sg_ids: aStringList,
  layer_names: aStringList,
  invoke_lambda_arns: aStringList,
  lambda_integration_arns: aStringList,
  grant_invoke_lambda_arns: aStringList,
  sqs_send_policies: aStringList,
  sqs_receive_policies: aStringList,
};

/** Group containers whose `defaults` and `lambdas` entries both hold lambda fields. */
const LAMBDA_GROUP_KEYS = [
  'endpoint_lambdas',
  'authorizer_lambdas',
  'queue_consumer_lambdas',
  'managed_lambdas',
];

export interface ConfigFieldError {
  /** Dotted path to the offending key, e.g. `endpoint_lambdas.lambdas.lexid_create_org.memory_size`. */
  path: string;
  error: string;
}

/**
 * Type-check every lambda field present anywhere in a config.json.
 *
 * Returns `[]` for anything it does not recognise, so a config that is not an object keeps failing
 * on the existing "must be a JSON object" check rather than growing a second, less helpful error.
 */
export function validateConfigFields(config: unknown): ConfigFieldError[] {
  if (!isRecord(config)) {
    return [];
  }

  const errors: ConfigFieldError[] = [];

  // A standalone lambda (phx-lambda-v1) is one flat object; the group templates keep their fields
  // nested. Checking the top level unconditionally covers the first and costs nothing for the rest,
  // since a group container is an object and fails none of these checks.
  checkFields(config, '', errors);

  for (const groupKey of LAMBDA_GROUP_KEYS) {
    const group = config[groupKey];
    if (!isRecord(group)) {
      continue;
    }
    checkFields(group, groupKey, errors);
    if (isRecord(group.defaults)) {
      checkFields(group.defaults, `${groupKey}.defaults`, errors);
    }
    const lambdas = isRecord(group.lambdas) ? group.lambdas : group;
    const prefix = isRecord(group.lambdas) ? `${groupKey}.lambdas` : groupKey;
    for (const [name, lambda] of Object.entries(lambdas)) {
      if (name !== 'defaults' && isRecord(lambda)) {
        checkFields(lambda, `${prefix}.${name}`, errors);
      }
    }
  }

  return errors;
}

function checkFields(
  holder: Record<string, unknown>,
  prefix: string,
  errors: ConfigFieldError[],
): void {
  for (const [key, check] of Object.entries(LAMBDA_FIELD_TYPES)) {
    if (!(key in holder) || holder[key] === null || holder[key] === undefined) {
      continue;
    }
    const problem = check(holder[key]);
    if (problem) {
      errors.push({ path: prefix ? `${prefix}.${key}` : key, error: `${key} ${problem}` });
    }
  }
}

/** Truncated so an error names the offending value without dumping a whole subtree. */
function describeValue(value: unknown): string {
  const text = JSON.stringify(value) ?? String(value);
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
}
